import styled from 'styled-components';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { spacing, colors, zIndex, easing, fadeIn } from 'ui/theme';
import { Delete } from 'ui/icons/action/Delete';
import { pxToRem } from 'utils/convert';
import { useTranslations } from '../../../hooks/useTranslations';
import { useDeleteImageOverlayContext } from '../../../contexts/DeleteImageOverlayContext';
import { useUploadAssetsContext } from 'modules/creativeLibrary/contexts/UploadAssetsContext';

const DeleteImageOverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${colors.backgroundScreen};
  justify-content: space-between;
  z-index: ${zIndex.modalOverlay};
  animation-duration: 200ms;
  animation-fill-mode: forwards;
  animation-name: ${fadeIn};
  animation-timing-function: ${easing.entrance};
  border-radius: ${spacing.threeQuarter};
`;

const BottomActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.half};
  background: ${colors.backgroundPrimary};
  border-top: 1px solid ${colors.borderPrimary};
  padding: ${spacing.plusHalf};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  border-radius: 0 0 ${spacing.threeQuarter} ${spacing.threeQuarter};
`;

const ActionText = styled.div`
  color: ${colors.textPrimary};
  font-size: ${pxToRem(14)};
  font-weight: 400;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const DeleteImageOverlay = () => {
  const { t } = useTranslations();
  const { imgsIdsToDelete, setImgsIdsToDelete } = useDeleteImageOverlayContext();
  const { removeSelectedUploads } = useUploadAssetsContext();
  const imgsToDeleteCount = imgsIdsToDelete.length;

  if (!imgsToDeleteCount) return null;

  const handleDelete = () => {
    removeSelectedUploads(imgsIdsToDelete);
    setImgsIdsToDelete([]);
  };

  const getDeleteButtonText = () => {
    return imgsToDeleteCount > 1
      ? t('caption.confirmDeleteImages', { count: imgsToDeleteCount })
      : t('caption.confirmDeleteImage');
  };

  return (
    <DeleteImageOverlayContainer>
      <BottomActionBar>
        <ActionText>
          <strong>{getDeleteButtonText()}?</strong>{' '}
          <span>
            {t(imgsToDeleteCount > 1 ? 'caption.imagesNotUploaded' : 'caption.imageNotUploaded')}
          </span>
        </ActionText>
        <ActionButtons>
          <Button
            onClick={() => {
              setImgsIdsToDelete([]);
            }}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.SECONDARY}
          >
            {t('button.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.DESTRUCTIVE}
          >
            <Delete />
            {getDeleteButtonText()}
          </Button>
        </ActionButtons>
      </BottomActionBar>
    </DeleteImageOverlayContainer>
  );
};
